<template>
  <div ref="rootRef" class="flex w-full flex-col gap-3 md:flex-row md:items-start md:gap-3">
    <!-- Combobox -->
    <div class="relative flex-1">
      <div class="relative">
        <input
          ref="inputRef"
          v-model="query"
          type="text"
          role="combobox"
          autocomplete="off"
          aria-autocomplete="list"
          :aria-expanded="isOpen"
          :placeholder="placeholderText"
          class="h-12 w-full rounded-lg border border-gray-400 bg-white px-3 py-2 pr-10 text-[16px] leading-[18px] text-gray-900 placeholder-gray-500 outline-none transition-colors focus:border-gray-500 focus:ring-1 focus:ring-gray-400"
          @focus="openDropdown"
          @input="onInput"
          @keydown.down.prevent="moveHighlight(1)"
          @keydown.up.prevent="moveHighlight(-1)"
          @keydown.enter.prevent="onEnter"
          @keydown.esc="closeDropdown"
        >

        <!-- Chevron: points down when closed, flips up when open -->
        <span class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
          <svg
            class="size-5 transition-transform duration-200"
            :class="{ 'rotate-180': isOpen }"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>

      <!-- Results dropdown -->
      <div
        v-if="isOpen"
        class="absolute left-0 right-0 top-[calc(100%+8px)] z-50 flex flex-col gap-1 rounded-lg bg-white p-2 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]"
      >
        <p v-if="loading" class="px-2 py-2 text-[14px] text-gray-500">
          {{ t('search.searching') }}
        </p>

        <p v-else-if="!results.length" class="px-2 py-2 text-[14px] text-gray-500">
          {{ t('search.no_results') }}
        </p>

        <template v-else>
          <p v-if="resultsSummary" class="px-2 pb-1 text-[13px] leading-4 text-gray-500">
            {{ resultsSummary }}
          </p>

          <!-- Show up to 7 results at a time; scroll for the rest. -->
          <div
            ref="resultsListRef"
            class="flex max-h-[276px] flex-col gap-1 overflow-y-auto"
          >
            <Link
              v-for="(result, index) in results"
              :key="`${result.type}-${result.url}`"
              :ref="el => setResultRef(el, index)"
              :href="result.url"
              class="flex h-9 shrink-0 items-center gap-2 rounded-lg px-2 no-underline transition-colors"
              :class="index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'"
              @mouseenter="highlightedIndex = index"
              @click="closeDropdown"
            >
              <span class="min-w-0 flex-1 truncate text-[16px] leading-[18px] text-gray-800">
                {{ result.label }}
              </span>
              <span class="flex shrink-0 items-center gap-1">
                <span class="whitespace-nowrap text-right text-[14px] leading-4 text-gray-900">
                  {{ result.typeLabel }}
                </span>
                <svg class="size-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 6 6 6-6 6" />
                </svg>
              </span>
            </Link>
          </div>
        </template>
      </div>
    </div>

    <button
      type="button"
      class="inline-flex h-12 w-full md:w-auto shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-[14px] text-[16px] font-semibold shadow-sm transition-opacity hover:opacity-90"
      :class="buttonVariant === 'outline' ? 'border bg-white' : 'text-white'"
      :style="buttonStyle"
      @click="goToSearchPage"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5 fill-current">
        <path d="M15.5 14h-.79l-.28-.27A6.5 6.5 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
      </svg>
      {{ buttonLabel }}
    </button>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import { themeColorHexFor, BRAND_GRADIENT } from '@/utils/siteThemeColors';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

const props = defineProps({
  placeholderText: { type: String, default: 'Wybierz lub wpisz' },
  buttonLabel:     { type: String, default: 'Szukaj' },
  // Page the "Szukaj" button navigates to. The current query is appended as ?q=.
  searchPath:      { type: String, default: '/szukaj' },
  // Pre-fills the input (used on the search results page to echo the active query).
  initialQuery:    { type: String, default: '' },
  // 'filled' = solid themed button (Search CTA); 'outline' = white/bordered (results page).
  buttonVariant:   { type: String, default: 'filled' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context:    { type: String, default: null },
});

const themeColor = computed(() => themeColorHexFor(props.site_context));

const buttonStyle = computed(() => (
  props.buttonVariant === 'outline'
    ? { color: themeColor.value, borderColor: themeColor.value }
    : { backgroundImage: BRAND_GRADIENT }
));

const rootRef = ref(null);
const inputRef = ref(null);
const resultsListRef = ref(null);
const resultRefs = [];

function setResultRef(el, index) {
  if (el) {
    resultRefs[index] = el.$el ?? el;
  }
}

// Keeps the highlighted item visible when arrow-key navigation moves the
// selection past the 7 rows shown in the scroll viewport.
function scrollHighlightedIntoView() {
  const el = resultRefs[highlightedIndex.value];
  if (el) el.scrollIntoView({ block: 'nearest' });
}
const query = ref(props.initialQuery || '');
const results = ref([]);
const loading = ref(false);
const isOpen = ref(false);
const highlightedIndex = ref(-1);

const COUNT_KEYS = {
  specialist: 'search.count_specialists',
  specialisation: 'search.count_specialisations',
  badanie: 'search.count_examinations',
  konsultacja_medyczna: 'search.count_consultations',
  zabieg: 'search.count_procedures',
};

const SUMMARY_ORDER = ['specialist', 'specialisation', 'badanie', 'konsultacja_medyczna', 'zabieg'];

const resultsSummary = computed(() => {
  if (!results.value.length) return '';

  const counts = {};
  for (const result of results.value) {
    const key = result.type === 'service' ? result.category : result.type;
    counts[key] = (counts[key] || 0) + 1;
  }

  const parts = [t('search.results_total', { count: results.value.length })];
  for (const key of SUMMARY_ORDER) {
    const count = counts[key];
    if (!count) continue;
    const labelKey = COUNT_KEYS[key];
    if (labelKey) parts.push(t(labelKey, { count }));
  }

  return parts.join(' · ');
});

let debounceTimer = null;
let requestToken = 0;

function openDropdown() {
  isOpen.value = true;
  if (!query.value.trim()) runSearch();
}

function closeDropdown() {
  isOpen.value = false;
  highlightedIndex.value = -1;
}

function onInput() {
  isOpen.value = true;
  highlightedIndex.value = -1;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runSearch, 200);
}

async function runSearch() {
  const term = query.value.trim();
  const token = ++requestToken;
  loading.value = true;
  try {
    const response = await fetch(`/api/search?q=${encodeURIComponent(term)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    if (token !== requestToken) return;
    resultRefs.length = 0;
    results.value = Array.isArray(payload.results) ? payload.results : [];
  } catch {
    if (token !== requestToken) return;
    results.value = [];
  } finally {
    if (token === requestToken) loading.value = false;
  }
}

function moveHighlight(step) {
  if (!isOpen.value) {
    isOpen.value = true;
    return;
  }
  if (!results.value.length) return;
  const count = results.value.length;
  highlightedIndex.value = (highlightedIndex.value + step + count) % count;
  nextTick(scrollHighlightedIntoView);
}

// Enter jumps to a highlighted autocomplete result when one is active; otherwise
// it behaves like pressing the "Szukaj" button (navigate to the search page).
function onEnter() {
  const result = results.value[highlightedIndex.value];
  if (result) {
    closeDropdown();
    router.visit(result.url);
    return;
  }
  goToSearchPage();
}

// "Szukaj" button: always navigates to the dedicated search results page.
// Empty query => no ?q param; a typed phrase => ?q=<phrase>.
function goToSearchPage() {
  closeDropdown();
  const term = query.value.trim();
  const target = term ? `${props.searchPath}?q=${encodeURIComponent(term)}` : props.searchPath;
  router.visit(target);
}

function handleClickOutside(event) {
  if (rootRef.value && !rootRef.value.contains(event.target)) {
    closeDropdown();
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>
